import { Entity } from "./data/common";
import { DataType } from "./data/enums";

export enum EntityRouteListIds {
  Transactions = "transactions-crud-list",
  Reconciliation = "reconciliation-crud-list",
  ManageClients = "clients-crud-list",
  PlaceholderForCrudLists = "placeholder-for-crud-lists",
  MatchingTransactions = "matching-transactions-crud-list",
  BankStatements = "bank-statements-crud-list",
  BankStatementUploads = "bank-statement-uploads-crud-list",
  CommunicationQueries = "communication-queries-crud-list"
}

export enum ListSearchComparator {
  Equal = "equal",
  Contains = "contains",
  GreaterThan = "greaterThan",
  GreaterThanOrEqual = "greaterThanOrEqual",
  LessThan = "lessThan",
  LessThanOrEqual = "lessThanOrEqual",
  NotEqual = "notEqual"
}

export interface ListSearchCriterion {
  comparator: ListSearchComparator;
  value: string | number | "true" | "false";
}

export interface ListSearchCriteria {
  [fieldName: string]: ListSearchCriterion | Array<ListSearchCriterion>;
}

export enum ListSearchOperator {
  And = "AND",
  Or = "OR"
}

export enum ListSize {
  TwentyItems = 20,
  FiftyItems = 50,
  HundredItems = 100,
  TwoHundredItems = 200
}

export interface ListSortCriteria {
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

export type ListBaselineSorts = Array<{
  sortBy: string;
  sortOrder: "ASC" | "DESC";
}>;

export interface ListLocalStorageConfig {
  mechanism: "sessionStorage";
  keyPrefix: string;
  searchCriteria?: boolean;
  statusesFilter?: boolean;
  sortCriteria?: boolean;
}

export enum OtherListIds {
  PlaceholderForNoneCrudLists = "placeholder-for-none-crud-lists"
}

// Types for list filter components

type ListFilterFieldName<E extends Entity> = Extract<keyof E, string>;

export interface OptionBase<T extends string | number> {
  value: T;
  label: string;
  isDisabled?: boolean;
  secondaryLabel?: string;
}

export type NumericOption = OptionBase<number>;

export type Option<T extends string = string> = OptionBase<T>;

export enum ListFilterFieldType {
  Checkbox = "checkbox",
  Date = "date",
  DateRange = "dateRange",
  SearchableEntitySelect = "searchableEntitySelect",
  Input = "input",
  Number = "number",
  Select = "select"
}

type ListFilterFieldValueTranslator = (value: any) => React.ReactNode;

interface BasicFilterDefinition {
  filterFieldType: ListFilterFieldType.Input | ListFilterFieldType.Number;
  options?: never;
  option?: never;
  dataType?: never;
  accessor?: never;
  searchFields?: never;
  valueTranslator?: never;
}

interface CheckboxFilterDefinition {
  filterFieldType: ListFilterFieldType.Checkbox;
  option: Option;
  options?: never;
  dataType?: never;
  accessor?: never;
  searchFields?: never;
  valueTranslator?: never;
}

interface DateFilterDefinition {
  filterFieldType: ListFilterFieldType.Date | ListFilterFieldType.DateRange;
  option?: never;
  options?: never;
  dataType?: never;
  accessor?: never;
  searchFields?: never;
  valueTranslator: ListFilterFieldValueTranslator;
}

interface SearchableEntitySelectFilterDefinition {
  filterFieldType: ListFilterFieldType.SearchableEntitySelect;
  options?: never;
  option?: never;
  dataType: DataType;
  accessor: string;
  searchFields: Array<string>;
  valueTranslator?: ListFilterFieldValueTranslator;
}

interface SelectFilterDefinition {
  filterFieldType: ListFilterFieldType.Select;
  options: Array<Option>;
  option?: never;
  dataType?: never;
  accessor?: never;
  searchFields?: never;
  valueTranslator?: ListFilterFieldValueTranslator;
}

type ListFilterFieldDefinition =
  | BasicFilterDefinition
  | CheckboxFilterDefinition
  | DateFilterDefinition
  | SearchableEntitySelectFilterDefinition
  | SelectFilterDefinition;

export type ListFilterDefinition<E extends Entity = Entity> =
  ListFilterFieldDefinition & {
    name: ListFilterFieldName<E>;
    label: string;
    helpText?: string;
  };
